import { format } from 'date-fns';
import { History } from 'history';
import { HopsResponseError } from '@xingternal/hops';
import { Dispatch } from 'redux';

import { ApiPathConfig } from '../../api/api-path-config';
import {
  dispatchableGet,
  dispatchablePut,
  dispatchablePost,
} from '../../api/redux-fetch';
import {
  CreatePage,
  PublishVersion,
  UpdateVersion,
} from '../../api/responses/api-body-types';
import {
  AdminTermsOverviewApiResponse,
  PublishVersionApiResponse,
  RevertVersionApiResponse,
  UpdateVersionApiResponse,
  CloneVersionApiResponse,
  PageWithVersionResponse,
} from '../../api/responses/api-response-types';
import {
  addErrorNotification,
  addInfoNotification,
} from '../../pages/shared/notification-store';
import { FormValues } from '../../pages/admin/terms/components/PageForm/types';
import { AppRoutes } from '../../RoutesConfig';
import { Page, PageDetails } from '../../models/page';
import { Version } from '../../models/version';

import {
  adminPagesSlice,
  adminPageSlice,
  saveVersionSlice,
  previewPageSlice,
  createPageSlice,
} from './slices';

const onErrorAction = (
  error: HopsResponseError,
  errorAction = saveVersionSlice.actions.error
) => (dispatch: Dispatch) => {
  dispatch(errorAction(error));
  const response = error.jsonResponse as { message: string };

  if (response && response.message) {
    addErrorNotification(response.message);
  }
};

const savePageAction = (payload: PageWithVersionResponse, message: string) => (
  dispatch: Dispatch
) => {
  dispatch(saveVersionSlice.actions.success(payload));
  dispatch(
    adminPageSlice.actions.save({ page: payload, version: payload.version })
  );
  addInfoNotification(message);
};

const clonePageAction = (
  payload: CloneVersionApiResponse,
  locale: string,
  history: History
) => (dispatch: Dispatch) => {
  const message = 'Version was cloned successfully';

  dispatch(saveVersionSlice.actions.success(payload));
  dispatch(
    adminPageSlice.actions.add({ page: payload, version: payload.version })
  );
  addInfoNotification(message);

  history.push(
    AppRoutes.admin.adminTermsOverview(payload.slug, payload.version.id, locale)
  );
};

export const clonePage = (page: Page, locale: string, history: History) => {
  return dispatchablePost<CloneVersionApiResponse, Record<string, unknown>>({
    url: ApiPathConfig.adminClonePage(page.id),
    onSuccess: (payload: CloneVersionApiResponse) =>
      clonePageAction(payload, locale, history),
    onLoading: saveVersionSlice.actions.loading,
    onError: (error: HopsResponseError) => onErrorAction(error),
    params: {},
  });
};

export const revertVersion = (version: Version) => {
  const params = {
    versionId: version.id,
  };

  const message = 'Back to Draft was successful';

  return dispatchablePut<RevertVersionApiResponse, PublishVersion>({
    url: ApiPathConfig.adminVersionRevert(version.id),
    onSuccess: (payload: RevertVersionApiResponse) =>
      savePageAction(payload, message),
    onLoading: saveVersionSlice.actions.loading,
    onError: (error: HopsResponseError) => onErrorAction(error),
    params,
  });
};

export const publishVersion = (version: Version) => {
  const params = {
    versionId: version.id,
  };

  const message = 'Version was published';

  return dispatchablePut<PublishVersionApiResponse, PublishVersion>({
    url: ApiPathConfig.adminVersionPublish(version.id),
    onSuccess: (payload: PublishVersionApiResponse) =>
      savePageAction(payload, message),
    onLoading: saveVersionSlice.actions.loading,
    onError: (error: HopsResponseError) => onErrorAction(error),
    params,
  });
};

export const saveVersion = (formValues: FormValues) => {
  const params = {
    versionId: formValues.id,
    rawText: formValues.rawText,
    name: formValues.name,
    groupName: formValues.groupName,
    locale: formValues.locale,
    liveDate: format(formValues.liveDate, 'yyyy-MM-dd'),
    includeGeneric: formValues.hasEmbedded,
    nestedCounterList: formValues.nestedCounterList,
    hideToc: formValues.hideToc,
  };

  const message = 'Form was updated successfully';

  return dispatchablePut<UpdateVersionApiResponse, UpdateVersion>({
    url: ApiPathConfig.adminVersionsUpdate(formValues.id),
    onSuccess: (payload: UpdateVersionApiResponse) =>
      savePageAction(payload, message),
    onLoading: saveVersionSlice.actions.loading,
    onError: (error: HopsResponseError) => onErrorAction(error),
    params,
  });
};

export const fetchAdminPage = (slug: string) => {
  return dispatchableGet<AdminTermsOverviewApiResponse>({
    url: ApiPathConfig.adminTermsDetails(slug),
    onSuccess: adminPageSlice.actions.success,
    onLoading: adminPageSlice.actions.loading,
    onError: adminPageSlice.actions.error,
  });
};

export const fetchAdminPages = () => {
  return dispatchableGet<Page>({
    url: ApiPathConfig.adminTermsIndex,
    onSuccess: adminPagesSlice.actions.success,
    onLoading: adminPagesSlice.actions.loading,
    onError: adminPagesSlice.actions.error,
  });
};

export const fetchPreviewPage = (
  versionId: string | number,
  genericVersionId: string | number,
  locale: string
) => {
  return dispatchableGet<PageDetails>({
    url: ApiPathConfig.adminShowPreview(versionId, genericVersionId, locale),
    onSuccess: previewPageSlice.actions.success,
    onLoading: previewPageSlice.actions.loading,
    onError: previewPageSlice.actions.error,
  });
};

const createPageAction = (payload: Page, history: History) => (
  dispatch: Dispatch
) => {
  const { slug, lastVersionId } = payload;
  history.push(AppRoutes.admin.adminTermsOverview(slug, lastVersionId, 'en'));

  dispatch(createPageSlice.actions.success(payload));
};

export const createPage = (formValues: CreatePage, history: History) => {
  return dispatchablePost<Page, CreatePage>({
    url: ApiPathConfig.createPage,
    onSuccess: (payload: Page) => createPageAction(payload, history),
    onLoading: createPageSlice.actions.loading,
    onError: (error: HopsResponseError) =>
      onErrorAction(error, createPageSlice.actions.error),
    params: formValues,
  });
};
